import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { EPISTEMICO } from "@/content/site";

const STATUS = { sugerido: "Sugerido", "em-revisao": "Em revisão", aprovado: "Aprovado", publicado: "Publicado", arquivado: "Arquivado" };
const TIPOS = { "paper-revisado": "Artigo revisado por pares", preprint: "Preprint", "relatorio-tecnico": "Relatório técnico/científico", "dados-institucionais": "Dados de instituição de pesquisa", universidade: "Universidade/instituto", "orgao-publico": "Órgão/base pública oficial", jornalismo: "Jornalismo (fonte contextual)" };
const ANALISE = [
    ["investigated", "O que o estudo investigou?"], ["method", "Como investigou?"], ["results", "O que encontrou? (principais resultados)"],
    ["not_shown", "O que não demonstrou?"], ["limitations", "Quais são as limitações?"], ["novelty", "O que mudou em relação ao que já estava publicado?"],
    ["contradictions", "Evidências contraditórias ou qualificadoras / contradições com a literatura"], ["agenda_relation", "Relação com minha agenda"],
    ["new_questions", "Novas perguntas"], ["teaching_use", "Possível uso em aula"],
];
const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ title: "", short_title: "", authors: "", venue: "", published_on: today(), doi: "", url: "", source_type: "paper-revisado", theme: "", epistemic: "evidencia", summary: "", analysis: {}, status: "sugerido" });
const inp = "[color-scheme:dark] w-full bg-transparent border-b border-[#3a5a4a] focus:border-[#EAEAE6] outline-none px-1 py-2 text-[#EAEAE6] font-sans text-[14px]";
const area = "w-full bg-transparent border border-[#3a5a4a] focus:border-[#EAEAE6] outline-none p-3 text-[#EAEAE6] font-sans text-[13.5px] leading-relaxed";

const F = ({ label, children }) => (
    <label className="flex flex-col gap-2">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#7E8978] font-sans">{label}</span>
        {children}
    </label>
);

const Sel = ({ value, onChange, options, testId }) => (
    <select data-testid={testId} value={value} onChange={onChange} className={`${inp} bg-[#0E2018]`}>
        {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
);

const AdminScience = () => {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [filter, setFilter] = useState("");

    const load = async () => setItems((await api.get("/science")).data);
    useEffect(() => {
        load();
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const setA = (k) => (e) => setForm({ ...form, analysis: { ...form.analysis, [k]: e.target.value } });

    const save = async (e) => {
        e?.preventDefault();
        setMsg("");
        try {
            const body = { ...form };
            delete body.id;
            const r = form.id ? await api.put(`/science/${form.id}`, body) : await api.post("/science", body);
            await load();
            if (e) {
                setForm(null);
                setMsg("");
                return r.data;
            }
            setForm({ ...form, ...r.data });
            return r.data;
        } catch (err) {
            const d = err?.response?.data?.detail;
            setMsg(typeof d === "string" ? d : "Erro ao salvar.");
            return null;
        }
    };

    const draft = async () => {
        setBusy(true);
        setMsg("Gerando rascunho com IA (pode levar até 1 minuto)…");
        const saved = form.id ? form : await save();
        if (!saved) return setBusy(false);
        try {
            const r = await api.post(`/science/${saved.id}/ai-draft`);
            setForm({ ...form, ...r.data });
            setMsg(`Rascunho gerado. ${r.data.source_text_found ? "" : "Não foi possível ler a página original — rascunho baseado só no resumo. "}Classificação sugerida pela IA: ${EPISTEMICO[r.data.epistemic_suggestion] || "—"}.`);
            await load();
        } catch (err) {
            setMsg(err?.response?.data?.detail || "Falha ao gerar rascunho.");
        }
        setBusy(false);
    };

    const remove = async (id) => {
        if (!window.confirm("Excluir esta pesquisa da Revisão Científica?")) return;
        await api.delete(`/science/${id}`);
        setForm(null);
        await load();
    };

    if (form) {
        return (
            <main data-testid="admin-science-form" className="max-w-3xl mx-auto px-6 py-14">
                <button type="button" onClick={() => setForm(null)} data-testid="science-back" className="font-sans text-[11px] uppercase tracking-[0.16em] text-[#7E8978] hover:text-[#EAEAE6] mb-8">← voltar à lista</button>
                <form onSubmit={save} className="flex flex-col gap-6">
                    <F label="título original"><input required data-testid="science-title" value={form.title} onChange={set("title")} className={`${inp} font-serif text-[18px]`} /></F>
                    <F label="título resumido (faixa Últimas Pesquisas)"><input data-testid="science-short-title" value={form.short_title} onChange={set("short_title")} className={inp} /></F>
                    <F label="autores"><input data-testid="science-authors" value={form.authors} onChange={set("authors")} className={inp} /></F>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <F label="periódico / instituição"><input required data-testid="science-venue" value={form.venue} onChange={set("venue")} className={inp} /></F>
                        <F label="data"><input type="date" required data-testid="science-date" value={form.published_on} onChange={set("published_on")} className={inp} /></F>
                        <F label="DOI"><input data-testid="science-doi" value={form.doi} onChange={set("doi")} className={inp} /></F>
                        <F label="URL original"><input type="url" data-testid="science-url" value={form.url} onChange={set("url")} className={inp} /></F>
                        <F label="tipo de fonte"><Sel testId="science-source-type" value={form.source_type} onChange={set("source_type")} options={TIPOS} /></F>
                        <F label="classificação epistêmica"><Sel testId="science-epistemic" value={form.epistemic} onChange={set("epistemic")} options={EPISTEMICO} /></F>
                        <F label="tema"><input data-testid="science-theme" value={form.theme} onChange={set("theme")} className={inp} /></F>
                        <F label="status"><Sel testId="science-status" value={form.status} onChange={set("status")} options={STATUS} /></F>
                    </div>
                    <F label="resumo"><textarea rows={4} data-testid="science-summary" value={form.summary} onChange={set("summary")} className={area} /></F>

                    <div className="mt-6 pt-8 border-t border-[#1B3025] flex flex-wrap items-center justify-between gap-4">
                        <p className="font-serif text-[20px] text-[#EAEAE6]">Análise</p>
                        <button type="button" disabled={busy} onClick={draft} data-testid="science-ai-draft" className="font-sans text-[11px] uppercase tracking-[0.16em] text-[#C7A659] border border-[#C7A659]/50 rounded-full px-4 py-2 hover:bg-[#C7A659]/10 disabled:opacity-50">
                            {busy ? "gerando…" : "Gerar rascunho com IA"}
                        </button>
                    </div>
                    {form.ai_draft && (
                        <p data-testid="science-ai-warning" className="font-sans text-[12px] text-[#d9a46b] border border-[#d9a46b]/40 px-4 py-3">
                            Rascunho gerado por IA — revisão humana necessária. Confira cada campo na fonte original antes de aprovar.
                        </p>
                    )}
                    {ANALISE.map(([k, l]) => (
                        <F key={k} label={l}><textarea rows={3} data-testid={`science-analysis-${k}`} value={form.analysis?.[k] || ""} onChange={setA(k)} className={area} /></F>
                    ))}
                    {msg && <p data-testid="science-msg" className="font-sans text-[12.5px] text-[#B0B6A6]">{msg}</p>}
                    <div className="flex items-center gap-6">
                        <button type="submit" data-testid="science-save" className="font-sans text-[13px] uppercase tracking-[0.06em] text-[#EAEAE6] border-b border-[#3a5a4a] hover:border-[#EAEAE6] pb-2">Salvar →</button>
                        {form.id && <button type="button" data-testid="science-delete" onClick={() => remove(form.id)} className="font-sans text-[12px] text-[#7E8978] hover:text-[#EAEAE6]">excluir</button>}
                    </div>
                    <p className="font-sans text-[11.5px] text-[#5A6859]">Somente itens com status “Publicado” aparecem na faixa Últimas Pesquisas. Nada é publicado automaticamente.</p>
                </form>
            </main>
        );
    }

    const shown = items.filter((i) => !filter || i.status === filter);
    return (
        <main data-testid="admin-science" className="max-w-3xl mx-auto px-6 py-14">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <p className="font-serif text-[22px] text-[#EAEAE6]">Revisão Científica</p>
                <button type="button" onClick={() => setForm(empty())} data-testid="science-new" className="font-sans text-[11px] uppercase tracking-[0.16em] text-[#C7A659] hover:text-[#e2c886]">+ nova pesquisa</button>
            </div>
            <div className="flex flex-wrap gap-2 mb-8">
                {[["", "Todas"], ...Object.entries(STATUS)].map(([k, l]) => (
                    <button key={l} type="button" data-testid={`science-filter-${k || "todas"}`} onClick={() => setFilter(k)} className={`font-sans text-[10.5px] uppercase tracking-[0.14em] px-3 py-1.5 border rounded-full ${filter === k ? "border-[#C7A659] text-[#C7A659]" : "border-[#234135] text-[#7E8978]"}`}>
                        {l} ({k ? items.filter((i) => i.status === k).length : items.length})
                    </button>
                ))}
            </div>
            <ul className="flex flex-col">
                {shown.map((i) => (
                    <li key={i.id} className="border-b border-[#1B3025] py-5">
                        <button type="button" onClick={() => setForm(i)} data-testid={`science-item-${i.id}`} className="text-left w-full">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-[#7E8978] font-sans">{i.published_on} · {i.venue} · {STATUS[i.status]} · {EPISTEMICO[i.epistemic]}{i.ai_draft ? " · rascunho IA" : ""}</p>
                            <p className="mt-1 font-serif text-[18px] text-[#EAEAE6]">{i.title}</p>
                        </button>
                    </li>
                ))}
            </ul>
        </main>
    );
};

export default AdminScience;
